import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box } from '@usealloy/component-library';
import { UserContext } from '~/dashboard/context';
import { usePolicyContext } from './usePolicyContext';
import type { Dimension } from '~/application-review/redux/types';

const IFRAME_DEFAULT_HEIGHT = 140;

interface AiSummaryCardProps {
  token: string;
  type?: string;
  inline?: boolean;
  style?: React.CSSProperties;
  /** Entity token for fetching rule explainability context */
  entityToken?: string;
  /** Workflow token for fetching workflow data */
  workflowToken?: string;
  /** Workflow version number */
  workflowVersion?: number;
  /** Whether this is a portfolio evaluation */
  isPortfolioEvaluation?: boolean;
  /** Dimensions for portfolio evaluations */
  dimensions?: Dimension[];
}

export const AiSummaryCard = ({
  token,
  type,
  inline = false,
  style = {},
  entityToken,
  workflowToken,
  workflowVersion,
  isPortfolioEvaluation = false,
  dimensions = [],
}: AiSummaryCardProps) => {
  const {
    featureFlags: { llmEvaluationSummaryEmbedded, llmSummariesJa },
    agent: { email, isEmployee },
  } = useContext(UserContext);

  const isJourneyApplication = /^JA-/.test(token);
  const baseCanView = llmEvaluationSummaryEmbedded && (
    isEmployee || email.endsWith('+ai@alloy.com')
  );
  const canView = baseCanView && (
    !isJourneyApplication || llmSummariesJa
  );

  // Determine if we should fetch and send policy context
  const shouldUsePolicyContext = Boolean(
    entityToken && workflowToken && workflowVersion,
  );

  const queryParams = new URLSearchParams();

  if (type) {
    queryParams.set('type', type);
  }

  if (inline) {
    queryParams.set('inline', 'true');
  }

  // Add policyContext param to signal embedded dashboard to wait for postMessage
  if (shouldUsePolicyContext) {
    queryParams.set('policyContext', 'true');
  }

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(IFRAME_DEFAULT_HEIGHT);
  const [iframeReady, setIframeReady] = useState<boolean>(false);

  // Use policy context hook to generate and send rule document
  const {
    ruleDocument,
    isLoading: policyContextLoading,
    sendToIframe,
  } = usePolicyContext({
    entityToken: entityToken || '',
    evaluationToken: token,
    workflowToken: workflowToken || null,
    workflowVersion: workflowVersion ?? null,
    isPortfolioEvaluation,
    dimensions,
    enabled: shouldUsePolicyContext,
  });

  const onMessage = useCallback(({ data: message, origin, source }) => {
    if (source !== iframeRef.current?.contentWindow) {
      return;
    }

    if (origin !== window.location.origin) {
      return;
    }

    if (message?.source !== 'embedded-dashboard') {
      return;
    }

    switch (message.type) {
      case 'resize':
        setIframeHeight(message.data.height);
        break;
      case 'readyForPolicyContext':
        // Iframe is ready to receive policy context
        setIframeReady(true);
        break;
      default:
        break;
    }
  }, [setIframeHeight]);

  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  // Send policy context to iframe when ready
  useEffect(() => {
    if (
      shouldUsePolicyContext
      && iframeReady
      && ruleDocument
      && !policyContextLoading
    ) {
      sendToIframe(iframeRef);
    }
  }, [shouldUsePolicyContext, iframeReady, ruleDocument, policyContextLoading, sendToIframe]);

  if (!canView) {
    return null;
  }

  return (
    <Box style={{ width: '100%', ...style }}>
      <iframe
        ref={iframeRef}
        width="100%"
        height={iframeHeight}
        style={{ border: 'none', marginBottom: -12 }}
        src={`${window.origin}/embedded/summary/${token}?${queryParams.toString()}`}
      />
    </Box>
  );
};
